import {DealerResponse, useCompanies, useDealer, useGetImage, useUser} from '@api'
import {FormFieldType, ImageType, NavigationPathKey, TranslationKey} from '@enums'
import {pageLinksAtom} from '@state'

import {useEffect, useState} from 'react'

import {countries, FormFieldItem, roles, useViewNavigate} from 'common'
import {useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

export function useUserViewModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [dealer, setDealer] = useState<DealerResponse | null>(null)
  const [companies, setCompanies] = useState<string[]>([])
  const navigate = useViewNavigate()
  const {id} = useParams()
  const {t} = useTranslation()

  const {data} = useUser(id)
  const {data: dealerData} = useDealer(data?.dealerId)
  const {data: companiesData} = useCompanies()

  const {data: image} = useGetImage(ImageType.USER, id)

  useEffect(() => {
    if (data?.dealerId && dealerData) {
      setDealer(dealerData)
    }
    if (data?.companyIds) {
      setCompanies(
        companiesData
          ? companiesData
              ?.filter(item => data.companyIds.includes(item.id))
              .map(item => `${item.name} (${countries(t).find(country => country.isoCode === item.country)?.name})`)
          : [],
      )
    }
  }, [data, dealerData, companiesData])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey['Products and accessories']), link: NavigationPathKey.PRODUCT_DASHBOARD},
        {title: t(TranslationKey['User search']), link: NavigationPathKey.USER_SEARCH},
        {title: t(TranslationKey['User view'])},
      ]),
    [],
  )

  /**
   * User form fields configuration - general section
   */
  const userGeneralFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Full name']),
      text: data?.fullName,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Email),
      text: data?.email,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Active),
      text: data?.active ? t(TranslationKey.Active) : t(TranslationKey.Inactive),
    },
  ]

  /**
   * User form fields configuration - permissions section
   */
  const userPermissionsFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Role),
      text: roles(t).find(item => item.code === data?.role)?.name,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Company),
      text: companies.length ? companies.join('\n') : '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Dealer),
      text: dealer?.name || '-',
    },
  ]

  /**
   * A function that handles the Back button click
   */
  const onPressBack = () => {
    navigate(NavigationPathKey.USER_SEARCH)
  }

  /**
   * A function that handles the Edit button click
   */
  const onPressEdit = () => {
    navigate(`${NavigationPathKey.USER_CREATION}/${id}`)
  }

  return {
    navigate,
    onPressEdit,
    onPressBack,
    t,
    image,
    userPermissionsFormFields,
    userGeneralFormFields,
  }
}
